import { useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { getBgGradientById } from 'eds-common';
import { useGetMeApi } from 'eds-common/api/v2';
import { useGetClassesApi } from 'eds-common/api/v3';
import { cn } from '@data/utils/cn/cn';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { Metrics } from '@data/components/composite/Metrics/Metrics';
import { type MetricV2Type } from '@data/config/metrics';
import {
  useMetricsBatchV2Api,
  type MetricBatchV2Response,
} from '@data/hooks/api/use-metrics-batch-v2-api/use-metrics-batch-v2-api';
import ReportBackButtonIcon from '@data/assets/reportbackbutton.svg';
import { Drilldown } from '@data/main';
import { getEdApps } from '@data/utils/get-ed-apps';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import { Tooltip } from '@scholastic/mockingjay';
import { useGetStudentsByClassAndEdAppApi } from '@data/hooks/api/use-get-students-by-class-and-ed-app-api/use-get-students-by-class-and-ed-app-api';
import {
  useEffectiveClassIds,
  useEffectiveStudentIds,
} from '@data/hooks/use-effective-filters/use-effective-filters';

interface HighlightsHeader {
  title: string;
  studentCount?: number | string;
  backTooltip: string;
}

function getStudentHeader(
  students:
    | {
        id: string;
        family_name: string;
        given_name: string;
        middle_name?: string;
      }[]
    | undefined,
  studentId: string | undefined
): HighlightsHeader {
  const student = students?.find((s) => s.id === studentId);
  const title = student
    ? [student.given_name, student.middle_name, student.family_name]
        .filter(Boolean)
        .join(' ')
    : 'Student';
  return { title, backTooltip: '' };
}

function getClassHeader(
  classes:
    | { class_id: string; name: string; studentIds: string[] }[]
    | undefined,
  classId: string,
  selectedStudentCount: number,
  entitledStudentsCount: number | string
): HighlightsHeader {
  const cls = classes?.find((c) => c.class_id === classId);
  const studentCount =
    selectedStudentCount > 0 ? selectedStudentCount : entitledStudentsCount;
  return {
    title: cls?.name ?? 'Class',
    studentCount,
    backTooltip: 'Back to All Classes',
  };
}

function getAllClassesHeader(
  classes: { class_id: string; name: string }[] | undefined,
  classIds: string[]
): HighlightsHeader {
  if (classIds.length === 0) {
    return { title: 'All Classes', backTooltip: '' };
  }
  if (classIds.length === 1) {
    const cls = classes?.find((c) => c.class_id === classIds[0]);
    return { title: cls?.name ?? '1 Class', backTooltip: '' };
  }
  return { title: `${classIds.length} Classes`, backTooltip: '' };
}

function useHighlightsHeader(): HighlightsHeader {
  const { drilldown, productId: edApp } = useReportOutletContext();
  const [searchParams] = useSearchParams();
  const { data: user } = useGetMeApi();

  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');
  const classId = classIds[0] ?? '';

  const { data: classes } = useGetClassesApi(
    {
      userId: user?.userId ?? '',
      orgId: user?.currentOrg.orgId ?? '',
      source: user?.userSource ?? 'Ednition',
      studentsExists: true,
    },
    { enabled: !!user }
  );

  // Get students entitled to the edApp to display the correct count in the header
  // Enable fetch when StudentDropdown is visible
  const { data: students } = useGetStudentsByClassAndEdAppApi(
    {
      classId: drilldown !== Drilldown.AllClasses ? classId : '',
      edApp,
    },
    {
      enabled:
        drilldown === Drilldown.Class ||
        drilldown === Drilldown.Student ||
        (classIds.length === 1 && drilldown === Drilldown.EngagementFeed),
    }
  );

  if (drilldown === Drilldown.Student) {
    const cls = classes?.find((c) => c.class_id === classId);
    const header = getStudentHeader(students, studentIds[0]);
    return { ...header, backTooltip: `Back to ${cls?.name ?? 'Class'}` };
  }

  if (drilldown === Drilldown.Class) {
    return getClassHeader(
      classes,
      classId,
      studentIds.length,
      students?.length ?? '—' // students.length will only be undefined while fetching
    );
  }

  return getAllClassesHeader(classes, classIds);
}

export { type MetricV2Type } from '@data/config/metrics';

interface ReportHighlightsProps {
  primaryMetricTypes?: MetricV2Type[];
  secondaryMetricTypes?: MetricV2Type[];
}

/**
 * Every metric on the page in one request.
 *
 * The primary and secondary sections are asked for together rather than one
 * batch each: the two share a filter, and the service answers metrics reading
 * the same table from a single query, so splitting them costs a second round
 * trip and buys nothing. Results are looked up by metric type, so each section
 * still renders only its own.
 */
function useMetrics(metricTypes: MetricV2Type[]) {
  const { dateRange, drilldown, productId } = useReportOutletContext();
  const [searchParams] = useSearchParams();
  const { data: user } = useGetMeApi();

  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');

  const effectiveClassIds = useEffectiveClassIds({
    orgId: user?.currentOrg.orgId ?? '',
    userId: user?.userId ?? '',
    edApp: productId,
    selectedClassIds: classIds,
  });
  const { studentIds: effectiveStudentIds, isReady: studentsReady } =
    useEffectiveStudentIds({
      drilldown,
      classIds,
      edApp: productId,
      selectedStudentIds: studentIds,
    });

  const { data, isLoading } = useMetricsBatchV2Api({
    orgId: user?.currentOrg.orgId ?? '',
    metricTypes,
    teacherIds: user ? [user.userId] : [],
    startDate: dateRange.from,
    endDate: dateRange.to,
    edApps: getEdApps(productId),
    classIds: effectiveClassIds,
    studentIds: effectiveStudentIds,
    // No request until the filters name their own ids.
    enabled: !!user && effectiveClassIds.length > 0 && studentsReady,
  });

  const byMetricType = useMemo(
    () => new Map((data ?? []).map((metric) => [metric.metricType, metric])),
    [data]
  );

  const select = useMemo(
    () => (types: MetricV2Type[]) =>
      types
        .map((metricType) => byMetricType.get(metricType))
        .filter((metric): metric is MetricBatchV2Response => !!metric),
    [byMetricType]
  );

  return { select, isLoading };
}

function PrimaryMetricsSection({
  metricTypes,
  metrics,
  isLoading,
}: {
  metricTypes: MetricV2Type[];
  metrics: MetricBatchV2Response[];
  isLoading: boolean;
}) {
  return (
    <div className="mt-4">
      <Metrics
        metrics={metrics}
        isLoading={isLoading}
        count={metricTypes.length}
      />
    </div>
  );
}

function SecondaryMetricsSection({
  metricTypes,
  metrics,
  isLoading,
}: {
  metricTypes: MetricV2Type[];
  metrics: MetricBatchV2Response[];
  isLoading: boolean;
}) {
  return (
    <Metrics
      metrics={metrics}
      isLoading={isLoading}
      count={metricTypes.length}
      variant="secondary"
    />
  );
}

export function ReportHighlights({
  primaryMetricTypes = [],
  secondaryMetricTypes = [],
}: ReportHighlightsProps) {
  const { drilldown, setDateRange } = useReportOutletContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const classId = searchParams.getAll('classId')[0];
  const { title, studentCount, backTooltip } = useHighlightsHeader();

  // One request covers both sections; each renders the slice it asked for.
  const { select, isLoading } = useMetrics([
    ...primaryMetricTypes,
    ...secondaryMetricTypes,
  ]);

  const showGradient =
    (drilldown === Drilldown.Class || drilldown === Drilldown.Student) &&
    !!classId;
  const showBackButton =
    drilldown === Drilldown.Class || drilldown === Drilldown.Student;

  const handleBack = () => {
    if (!classId) return;
    // The skill picked on Skill Performance outlasts the trip back.
    const skillId = searchParams.get('skillId');
    const skillParam = skillId ? `skillId=${skillId}` : '';
    if (drilldown === Drilldown.Student) {
      const parentPath = pathname.replace(/\/student$/, '/class');
      const skillSuffix = skillParam ? `&${skillParam}` : '';
      navigate(`${parentPath}?classId=${classId}${skillSuffix}`);
    } else if (drilldown === Drilldown.Class) {
      const parentPath = pathname.replace(/\/class$/, '');
      navigate(skillParam ? `${parentPath}?${skillParam}` : parentPath);
    }
    setDateRange(getSchoolYearDateRange());
  };

  return (
    <div className="overflow-hidden rounded-lg shadow-sm">
      {/* Gradient bar at class/student levels */}
      {showGradient && (
        <div
          className={cn('h-3 w-full', getBgGradientById(classId))}
          aria-hidden
        />
      )}

      {/* Title area */}
      <div className="px-4 py-6">
        <div className="mb-4 flex">
          {showBackButton && (
            <Tooltip content={backTooltip}>
              <button
                type="button"
                onClick={handleBack}
                aria-label={backTooltip}
                className="mr-2 mt-1 self-start [&:hover_svg_path:first-child]:!fill-[#E8E8E8] "
              >
                <ReportBackButtonIcon aria-hidden className="h-6 w-6" />
              </button>
            </Tooltip>
          )}
          <div>
            <h3 className="text-xl font-bold text-neutral-1300 tablet:text-2xl">
              Highlights: {title}
            </h3>
            {studentCount && (
              <p className="neutral-1300 mt-2 text-xs font-bold">
                Students
                <span className="ml-2 text-[14px] font-normal">
                  {studentCount}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 h-[1px] w-full bg-neutral-400" />

        {/* Primary metrics */}
        {primaryMetricTypes.length > 0 && (
          <PrimaryMetricsSection
            metricTypes={primaryMetricTypes}
            metrics={select(primaryMetricTypes)}
            isLoading={isLoading}
          />
        )}

        {/* Secondary metrics */}
        {secondaryMetricTypes.length > 0 && (
          <SecondaryMetricsSection
            metricTypes={secondaryMetricTypes}
            metrics={select(secondaryMetricTypes)}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
}
